import { useState, useEffect, useRef, useCallback } from 'react'
import { Check, Copy, Loader2, AlertCircle, RefreshCw, Terminal } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import Modal from './Modal'
import { routersApi } from '../../services/managedDevicesApi'
import { isVpnHandshakeFresh } from '../../utils/vpn'

const POLL_MS = 4000
// Depois do callback do script, prazo pro servidor registrar o handshake do túnel
// (handshake real ≤ ~2 min + sync do stats pro banco ≤ ~1 min).
const VPN_VERIFY_TIMEOUT_MS = 3 * 60 * 1000

export default function BootstrapRouterModal({ isOpen, onClose, router, onBootstrapDone }) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [activateResult, setActivateResult] = useState(null)
  const [copied, setCopied] = useState(null)
  const [done, setDone] = useState(false)
  const [vpnConnected, setVpnConnected] = useState(false)
  const [vpnTimedOut, setVpnTimedOut] = useState(false)
  const pollingRef = useRef(null)
  const doneAtRef = useRef(null)

  const routerId = router?.id

  const fetchCmd =
    activateResult?.bootstrapScriptUrl &&
    `/tool fetch url="${activateResult.bootstrapScriptUrl}" dst-path=bootstrap.rsc`

  const importCmd = '/import bootstrap.rsc'

  const activate = useCallback(async () => {
    if (!routerId) return
    setLoading(true)
    setError(null)
    setActivateResult(null)
    setDone(false)
    setVpnConnected(false)
    setVpnTimedOut(false)
    doneAtRef.current = null
    setCopied(null)
    try {
      const res = await routersApi.activateBootstrap(routerId)
      setActivateResult(res)
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('routers.bootstrapModal.generateError'))
    } finally {
      setLoading(false)
    }
  }, [routerId, t])

  useEffect(() => {
    if (!isOpen || !routerId) return
    activate()
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [isOpen, routerId, activate])

  useEffect(() => {
    if (!isOpen || !routerId || vpnConnected || vpnTimedOut) {
      if (pollingRef.current) clearInterval(pollingRef.current)
      return
    }

    // Duas fases: (1) o script rodou no MikroTik AGORA (bootstrapCompletedAt POSTERIOR ao
    // bootstrapRequestedAt deste link — o backend zera o completed no activate); (2) a VPN
    // conectou de fato (handshake reportado pelo servidor DEPOIS deste bootstrap). Assim um
    // re-bootstrap de device já conectado não declara sucesso instantâneo com dados velhos.
    const check = async () => {
      try {
        const r = await routersApi.getById(routerId)
        const requestedMs = r.bootstrapRequestedAt ? new Date(r.bootstrapRequestedAt).getTime() : null
        const completedMs = r.bootstrapCompletedAt ? new Date(r.bootstrapCompletedAt).getTime() : null
        const scriptRanNow = completedMs != null && (requestedMs == null || completedMs >= requestedMs)
        if (scriptRanNow && !doneAtRef.current) {
          doneAtRef.current = Date.now()
          setDone(true)
        }
        if (doneAtRef.current) {
          // Handshake tem que ser recente E posterior ao início deste bootstrap (ambos UTC do servidor).
          const hsMs = r.vpnPeerLastHandshake ? new Date(r.vpnPeerLastHandshake).getTime() : null
          const handshakeAfterBootstrap =
            hsMs != null && (requestedMs == null || hsMs >= requestedMs) && isVpnHandshakeFresh(r.vpnPeerLastHandshake)
          if (handshakeAfterBootstrap) {
            setVpnConnected(true)
            onBootstrapDone?.()
            if (pollingRef.current) clearInterval(pollingRef.current)
          } else if (Date.now() - doneAtRef.current > VPN_VERIFY_TIMEOUT_MS) {
            setVpnTimedOut(true)
            if (pollingRef.current) clearInterval(pollingRef.current)
          }
        }
      } catch {
        /* ignora */
      }
    }

    pollingRef.current = setInterval(check, POLL_MS)
    check()
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [isOpen, routerId, vpnConnected, vpnTimedOut, onBootstrapDone])

  const handleCopy = async (key, text) => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      const el = document.createElement('textarea')
      el.value = text
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
    }
    setCopied(key)
    setTimeout(() => setCopied((c) => (c === key ? null : c)), 3000)
  }

  const handleClose = () => {
    if (pollingRef.current) clearInterval(pollingRef.current)
    setActivateResult(null)
    setError(null)
    setDone(false)
    setVpnConnected(false)
    setVpnTimedOut(false)
    doneAtRef.current = null
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('routers.bootstrapModal.title', { name: router?.name || '' })}>
      <div className="space-y-5 max-w-xl">
        <p className="text-sm text-gray-600">
          <Trans
            i18nKey="routers.bootstrapModal.intro"
            values={{ minutes: activateResult?.expiresInMinutes ?? 60 }}
            components={{ code: <code className="text-xs bg-gray-100 px-1 rounded" /> }}
          />
        </p>

        {loading && (
          <div className="flex items-center gap-3 text-gray-600">
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('routers.bootstrapModal.generating')}
          </div>
        )}

        {error && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-800">{error}</p>
              <button type="button" className="mt-2 text-sm text-red-600 underline" onClick={activate}>
                {t('routers.bootstrapModal.retry')}
              </button>
            </div>
          </div>
        )}

        {activateResult && !loading && !error && (
          <>
            <p className="text-sm font-medium text-gray-800 flex items-center gap-2">
              <Terminal className="w-4 h-4 text-theme-primary" />
              {t('routers.bootstrapModal.terminalHint')}
            </p>

            {/* Passo 1 e Passo 2 em caixas separadas: o MikroTik não executa os dois
                comandos colados de uma vez (o /import roda antes do fetch terminar). */}
            <div>
              <p className="text-sm font-medium text-gray-800 mb-2">
                {t('routers.bootstrapModal.step1')}
              </p>
              <div className="relative">
                <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
                  {fetchCmd}
                </pre>
                <button
                  type="button"
                  onClick={() => handleCopy('fetch', fetchCmd)}
                  className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors"
                  title={t('routers.bootstrapModal.copy')}
                >
                  {copied === 'fetch' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-gray-300" />}
                </button>
              </div>
              {copied === 'fetch' && <p className="text-xs text-green-600 mt-1">{t('routers.bootstrapModal.copied')}</p>}
            </div>

            <div>
              <p className="text-sm font-medium text-gray-800 mb-2">
                {t('routers.bootstrapModal.step2')}
              </p>
              <div className="relative">
                <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
                  {importCmd}
                </pre>
                <button
                  type="button"
                  onClick={() => handleCopy('import', importCmd)}
                  className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors"
                  title={t('routers.bootstrapModal.copy')}
                >
                  {copied === 'import' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-gray-300" />}
                </button>
              </div>
              {copied === 'import' && <p className="text-xs text-green-600 mt-1">{t('routers.bootstrapModal.copied')}</p>}
            </div>

            <p className="text-xs text-gray-500">
              <Trans
                i18nKey="routers.bootstrapModal.httpsNote"
                components={{ fetch: <code className="bg-gray-100 px-1 rounded" /> }}
              />
            </p>

            <button
              type="button"
              onClick={activate}
              className="btn btn-secondary btn-sm inline-flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              {t('routers.bootstrapModal.generateNewLink')}
            </button>
          </>
        )}

        {done && !vpnConnected && !vpnTimedOut && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
            <Loader2 className="w-8 h-8 text-blue-600 mx-auto mb-2 animate-spin" />
            <p className="text-blue-800 font-medium">{t('routers.bootstrapModal.scriptDoneTitle')}</p>
            <p className="text-sm text-blue-700 mt-1">{t('routers.bootstrapModal.scriptDoneDetail')}</p>
          </div>
        )}

        {done && vpnConnected && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
            <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-green-800 font-medium">{t('routers.bootstrapModal.completedTitle')}</p>
            <p className="text-sm text-green-700 mt-1">{t('routers.bootstrapModal.completedDetail')}</p>
          </div>
        )}

        {done && vpnTimedOut && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-amber-800 font-medium">{t('routers.bootstrapModal.vpnFailedTitle')}</p>
              <p className="text-sm text-amber-700 mt-1">{t('routers.bootstrapModal.vpnFailedDetail')}</p>
              <button
                type="button"
                className="mt-2 btn btn-primary btn-sm inline-flex items-center gap-1"
                onClick={activate}
              >
                <RefreshCw className="w-3 h-3" />
                {t('routers.bootstrapModal.generateNewLink')}
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-2 border-t border-gray-200">
          <button type="button" className="btn btn-secondary" onClick={handleClose}>
            {t('routers.bootstrapModal.close')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
